import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, Injectable } from '@angular/core';
import { HttpModule } from '@angular/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientJsonpModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import {
  BciCoreModule, BciImprintComponent, BciLayoutModule, BciSharedModule, LogoutComponent,
  BreadcrumbsService
} from '@bci-web-core/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkTreeModule } from '@angular/cdk/tree';
/* Material imports*/
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatCheckboxModule, MatDialogModule, MatDatepickerModule,
  MatNativeDateModule, MatGridListModule, MatDialogRef,
  MAT_DIALOG_DATA, MatTreeModule, MatRadioModule, MatSortModule
} from '@angular/material';
import { MatStepperModule } from '@angular/material/stepper';
/* CDK imports */
import { CdkStepperModule } from '@angular/cdk/stepper';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
/* angular-highcharts */
import { ChartModule, HIGHCHARTS_MODULES } from 'angular-highcharts';
import stock from 'highcharts/modules/stock.src';
import more from 'highcharts/highcharts-more.src';
import exporting from 'highcharts/modules/exporting.src';
import draggable from 'highcharts/modules/draggable-points.src';
import solidGauge from 'highcharts/modules/solid-gauge.src';
/* platform-browser */
import { environment } from '../environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
/* Azure Active Directory */
import { AdalService, AdalGuard, AdalInterceptor } from 'adal-angular4';
/* App Service */
import { DataService } from './services/data.service';
import { DateService, WarningDialog, ConfirmationDialog, ControlListComponent, FabricService } from './shared/index';
import { UserService } from './shared/user-service.service';
import { NotificationService } from './services/notification-service.service';
import { PlantServiceService, EnergyService } from './services/index';
import { CommonService } from './services/common.service';

/* App components */
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './Components/user/login.component';
import { MdListComponent } from './Components/site/site-master/md-list/md-list.component';
import { HomeComponent } from './Components/home/home.component';
// Directive && PIPE
import { NumberOnlyDirective, PositiveDecimalNumberDirective, OperatorsPipe, FrequenciesPipe } from 'src/app/shared/index';
// Site
import { SiteDashboardComponent } from './Components/site/site-dashboard.component';
// Over View
import { OverViewComponent } from './Components/site/over-view/over-view.component';
// Energy
import {
  EnergyDashboardComponent, EnergyComponent, LoadOnTreeComponent,
  TestbenchOverviewComponent,
  RealTimeComponent, SpecificEnergyComponent, MeterViewComponent,
  TransformerMonitoringComponent, TransformerDashboardComponent,
  DeepDiveComponent, NotificationsComponent as NotificationsOnEnergyComponent,
  EnergyTrendsComponent, DataTagComponent
} from './Components/site/energy-dashboard/index';
// Utilites
import { UtilitiesComponent } from './Components/site/utilities/utilities.component';
// Pipes
import { MeterIdFilterPipe } from './shared/pipes/filters';
import { SelectRoleFilterPipe } from './shared/pipes/rolefilter.pipe';
// Solar Dashboard
import { SolarDashboardComponent } from './Components/site/solar-dashboard/solar-dashboard.component';
import { PlantOverviewComponent } from './Components/site/solar-dashboard/plant-overview/plant-overview.component';
import { InverterViewComponent } from './Components/site/solar-dashboard/inverter-view/inverter-view.component';
import { ArchitectureComponent } from './Components/site/solar-dashboard/architecture/architecture.component';
import { NotificationsComponent } from './Components/site/solar-dashboard/notifications/notifications.component';
import { PlantStatusComponent } from './Components/site/solar-dashboard/plant-overview/plant-status/plant-status.component';
import { PowerIrradianceComponent } from './Components/site/solar-dashboard/plant-overview/power-irradiance/power-irradiance.component';
// tslint:disable-next-line:max-line-length
import { PerformanceCharacteristicComponent } from './Components/site/solar-dashboard/plant-overview/performance-characteristic/performance-characteristic.component';
import { SolarEnergyComponent } from './Components/site/solar-dashboard/plant-overview/solar-energy/solar-energy.component';
import { SolarYieldComponent } from './Components/site/solar-dashboard/plant-overview/solar-yield/solar-yield.component';
import { PlantKeyComponent } from './Components/site/solar-dashboard/plant-overview/plant-key/plant-key.component';
import { SolarDailyReportComponent } from './Components/report/solar-daily-report/solar-daily-report.component';
import { SolarMonthlyReportComponent } from './Components/report/solar-monthly-report/solar-monthly-report.component';
// Machine Monitoring
import { MachineMonitoringComponent } from './Components/site/machine-monitoring/machine-monitoring.component';
// Kpi Benchmarking
import { KpiBenchmarkingComponent } from './Components/site/kpi-benchmarking/kpi-benchmarking.component';
//
/* Organization Chart Module */
import { OrganizationChartModule } from 'src/app/orgChart/components/organizationchart/organizationchart';
import { TreeChartModule } from 'src/app/orgChart/components/treechart/treechart';
// tslint:disable-next-line:max-line-length
import { SolarLongTermTrendComponent } from './Components/site/solar-dashboard/plant-overview/solar-long-term-trend/solar-long-term-trend.component';
import { ProjectInfoComponent } from './Components/site/project-info/project-info.component';
import { SiteInfoComponent } from './Components/site/site-info/site-info.component';
import { PlantPerformanceComponent } from './Components/site/plant-performance/plant-performance.component';

export function highchartsModules() {
  // apply Highcharts Modules to this array
  return [stock, more, exporting, draggable, solidGauge];
  // return [stock];
}
import { AngularSplitModule } from 'angular-split';
import { PopoverModule } from 'ngx-popover';

import { SiteMasterComponent } from './Components/site/site-master/site-master.component';
import { SiteDetailComponent } from './Components/site/site-master/site-detail/site-detail.component';
import { SiteCreationComponent } from './Components/site/site-master/site-creation/site-creation.component';
import { ConnectionDetailsComponent } from './Components/site/site-master/connection-details/connection-details.component';
import { ShiftDetailsComponent } from './Components/site/site-master/shift-details/shift-details.component';
import { SetupProjectComponent } from './Components/site/site-master/setup-project/setup-project.component';
import { ProcessAndLoadDetailsComponent } from './Components/site/site-master/process-and-load-details/process-and-load-details.component';
import { ReportGroupComponent } from './Components/site/site-master/report-group/report-group.component';
import { LoadMasterComponent } from './Components/site/site-master/load-master/load-master.component';
import { ReservesAndSourcesComponent } from './Components/site/site-master/reserves-and-sources/reserves-and-sources.component';
import { AlertsConfigurationComponent } from './Components/site/site-master/alerts-configuration/alerts-configuration.component';
import { WidgetConfigurationComponent } from './Components/site/site-master/widget-configuration/widget-configuration.component';
import { ReportConfigurationComponent } from './Components/site/site-master/report-configuration/report-configuration.component';
import { DataAcquisitionLayoutComponent } from './Components/site/site-master/data-acquisition-layout/data-acquisition-layout.component';
// tslint:disable-next-line:max-line-length
import { AssignMeterModalComponent } from './Components/site/site-master/data-acquisition-layout/assign-meter-modal/assign-meter-modal.component';
// tslint:disable-next-line:max-line-length
import { ClickAndNavigationDetailsComponent } from './Components/site/site-master/click-and-navigation-details/click-and-navigation-details.component';
import { TreeConfigurationComponent } from './Components/site/site-master/tree-configuration/tree-configuration.component';
// tslint:disable-next-line:max-line-length
import { UploadImageConfigurationComponent } from './Components/site/site-master/click-and-navigation-details/upload-image-configuration/upload-image-configuration.component';
// tslint:disable-next-line:max-line-length
import { UploadImageModalComponent } from './Components/site/site-master/click-and-navigation-details/upload-image-modal/upload-image-modal.component';
// tslint:disable-next-line:max-line-length
import { AddNodeDetailModalComponent } from './Components/site/site-master/click-and-navigation-details/add-node-detail-modal/add-node-detail-modal.component';
import { AddNodeComponent } from './Components/add/add-node/add-node.component';
import { AddDcuComponent } from './Components/add/add-dcu/add-dcu.component';
import { AddMeterComponent } from './Components/add/add-meter/add-meter.component';
import { NodeDetailComponent } from './Components/add/add-node/node-detail/node-detail.component';
import { JwtInterceptor, ErrorInterceptor } from './shared/interceptor';
import { PlantNavigationComponent } from './Components/site/over-view/plant-navigation/plant-navigation.component';
import { AddUserComponent } from './Components/add/add-user/add-user.component';
import { AddUserSuccessModalComponent } from './Components/add/add-user/add-user-success-modal/add-user-success-modal.component';
import { AppGuard } from './app.guard';
import { LoadOnViewConfigurationComponent } from './Components/site/site-master/tree-configuration/load-on-view-configuration/load-on-view-configuration.component';
import { InverterViewConfigurationComponent } from './Components/site/site-master/tree-configuration/inverter-view-configuration/inverter-view-configuration.component';
import { AddNodeTreeConfigurationModalComponent } from './Components/site/site-master/tree-configuration/add-node-tree-configuration-modal/add-node-tree-configuration-modal.component';
import { AddNodeInfoModalComponent } from './Components/site/site-master/click-and-navigation-details/add-node-info-modal/add-node-info-modal.component';
import { GenericTreeViewConfigurationComponent } from './Components/site/site-master/tree-configuration/generic-tree-view-configuration/generic-tree-view-configuration.component';
import { RoleManagementComponent } from './Components/role-management/role-management.component';
import { UserManagementComponent } from './Components/user/user-management/user-management.component';
import { AddEditUserModalComponent } from './Components/user/add-edit-user-modal/add-edit-user-modal.component';
import { SsoLoginModalComponent } from './Components/user/sso-login-modal/sso-login-modal.component';
import { FeatureSelectionModalComponent } from './Components/user/feature-selection-modal/feature-selection-modal.component';
import { SystemPrivacyModalComponent } from './Components/user/system-privacy-modal/system-privacy-modal.component';
import { SensorTrendModalComponent } from './Components/site/over-view/sensor-trend-modal/sensor-trend-modal.component';
import { AddLoadComponent } from './Components/add/add-load/add-load.component';
import { MeterInventoryComponent } from './Components/view/meter-inventory/meter-inventory.component';
import { LoadInventoryComponent } from './Components/view/load-inventory/load-inventory.component';
import { DcuInventoryComponent } from './Components/view/dcu-inventory/dcu-inventory.component';
import { ManageReportComponent } from './Components/report/manage-report/manage-report.component';
import { ManageSolarSchedulerComponent } from './Components/report/manage-solar-scheduler/manage-solar-scheduler.component';
import { HelpComponent } from './Components/help/help.component';
import { EqBenchmarkingComponent } from './Components/report/eq-benchmarking/eq-benchmarking.component';
import { SatDatepickerModule, SatNativeDateModule } from 'saturn-datepicker';
import { HashLocationStrategy, LocationStrategy} from '@angular/common';
import { ContactUsModalComponent } from './Components/user/contact-us-modal/contact-us-modal.component';
import { EnergyTreeExcelComponent } from './Components/report/energy-tree-excel/energy-tree-excel.component';
//Export
import { RealtimeDataComponent } from './Components/export/realtime-data/realtime-data.component';
import { AddBuildingComponent } from './Components/add/add-building/add-building/add-building.component';
import { UtilityConsumptionChartsComponent } from './Components/site/over-view/utility-consumptionCharts/utility-consumption-charts/utility-consumption-charts.component';

@NgModule({
  declarations: [
    AppComponent
    , LoginComponent
    , ConfirmationDialog
    , ControlListComponent
    , MdListComponent
    , HomeComponent,
    SolarDashboardComponent,
    PlantOverviewComponent,
    InverterViewComponent,
    ArchitectureComponent,
    NotificationsComponent,
    PlantStatusComponent,
    PowerIrradianceComponent,
    PerformanceCharacteristicComponent,
    SolarEnergyComponent,
    SolarYieldComponent,
    SolarLongTermTrendComponent,
    ProjectInfoComponent, SiteInfoComponent,
    PlantPerformanceComponent,
    PlantKeyComponent,
    SolarDailyReportComponent, SolarMonthlyReportComponent,
    EnergyDashboardComponent, TestbenchOverviewComponent,
    RealTimeComponent, DeepDiveComponent, MeterViewComponent,
    EnergyComponent, EnergyTrendsComponent,
    WarningDialog,
    DataTagComponent,
    SiteDashboardComponent,
    OverViewComponent,
    UtilitiesComponent,
    MachineMonitoringComponent,
    KpiBenchmarkingComponent,
    LoadOnTreeComponent,
    SpecificEnergyComponent,
    TransformerMonitoringComponent,
    TransformerDashboardComponent,
    NotificationsOnEnergyComponent,
    SiteMasterComponent,
    SiteDetailComponent,
    SiteCreationComponent,
    ConnectionDetailsComponent,
    ShiftDetailsComponent,
    SetupProjectComponent,
    // Directive
    NumberOnlyDirective,
    PositiveDecimalNumberDirective,
    OperatorsPipe,
    FrequenciesPipe,
    //
    ProcessAndLoadDetailsComponent,
    ReportGroupComponent,
    LoadMasterComponent,
    ReservesAndSourcesComponent,
    AlertsConfigurationComponent,
    ReservesAndSourcesComponent,
    WidgetConfigurationComponent,
    ReportConfigurationComponent,
    DataAcquisitionLayoutComponent,
    AssignMeterModalComponent,
    // Pipes
    MeterIdFilterPipe,
    SelectRoleFilterPipe,
    ClickAndNavigationDetailsComponent,
    TreeConfigurationComponent,
    UploadImageConfigurationComponent,
    UploadImageModalComponent,
    AddNodeDetailModalComponent,
    AddNodeComponent,
    AddDcuComponent,
    AddMeterComponent,
    NodeDetailComponent,
    PlantNavigationComponent,
    AddUserComponent,
    AddUserSuccessModalComponent,
    LoadOnViewConfigurationComponent,
    InverterViewConfigurationComponent,
    AddNodeTreeConfigurationModalComponent,
    AddNodeInfoModalComponent,

    RoleManagementComponent,
    GenericTreeViewConfigurationComponent,
    UserManagementComponent,
    AddEditUserModalComponent,
    SsoLoginModalComponent,
    FeatureSelectionModalComponent,
    SystemPrivacyModalComponent,
    SensorTrendModalComponent,
    AddLoadComponent,
    MeterInventoryComponent,
    LoadInventoryComponent,
    DcuInventoryComponent,
    ManageReportComponent,
    ManageSolarSchedulerComponent,
    HelpComponent,
    EqBenchmarkingComponent,
    ContactUsModalComponent,
    EnergyTreeExcelComponent,
    RealtimeDataComponent,
    AddBuildingComponent,
    UtilityConsumptionChartsComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpModule,
    HttpClientModule,
    HttpClientJsonpModule,
    FormsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BciCoreModule.forRoot({ prod_environment: environment.production, core_config_url: '/assets/config/config.json?v1.1' }),
    BciLayoutModule,
    BciSharedModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatTabsModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatCardModule,
    MatListModule,
    MatExpansionModule,
    MatSidenavModule,
    MatPaginatorModule,
    MatGridListModule,
    MatTreeModule,
    MatSortModule,
    MatMenuModule,
    MatStepperModule,
    CdkStepperModule,
    /* CDK */
    OverlayModule,
    PortalModule,
    ChartModule,
    OrganizationChartModule,
    TreeChartModule,
    NgxDaterangepickerMd.forRoot(),
    NgxMatSelectSearchModule,
    PopoverModule,
    CdkTreeModule,
    DragDropModule,
    AngularSplitModule.forRoot(),
    SatDatepickerModule, 
    SatNativeDateModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [DatePipe, DecimalPipe, CurrencyPipe, DataService, UserService,
    BreadcrumbsService, PlantServiceService, EnergyService, CommonService, NotificationService, DateService, FabricService,
    { provide: HIGHCHARTS_MODULES, useFactory: highchartsModules },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MatDialogRef, useValue: {} },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    AppGuard, AdalService, AdalGuard,
    [{provide: LocationStrategy, useClass: HashLocationStrategy}],
    // { provide: HTTP_INTERCEPTORS, useClass: AdalInterceptor, multi: true }

  ],
  bootstrap: [
    AppComponent
  ],
  entryComponents: [
    BciImprintComponent,
    LogoutComponent,
    ConfirmationDialog,
    WarningDialog,
    DataTagComponent,
    SetupProjectComponent,
    LoadMasterComponent,
    NodeDetailComponent,
    AssignMeterModalComponent,
    UploadImageModalComponent,
    AddNodeDetailModalComponent,
    AddUserSuccessModalComponent,
    AddNodeTreeConfigurationModalComponent,
    AddNodeInfoModalComponent,
    AddEditUserModalComponent,
    SsoLoginModalComponent,
    FeatureSelectionModalComponent,
    SystemPrivacyModalComponent,
    SensorTrendModalComponent,
    ContactUsModalComponent
  ]
})
export class AppModule { }
